/**
 * Where old personal URLs go now that Me has its own pages (navigation
 * rebuild, wave 3, 28 Oct 2026).
 *
 * The Me side gained /me/documents, /me/tax and /me/loans. The old URLs
 * stay as redirect stubs, because notification links stored in the
 * database (and bookmarks) point at them:
 *
 *   /payroll/tax-declaration  → /me/tax        (everyone: it was only ever
 *                                               the viewer's own declaration;
 *                                               the HR queue stays at
 *                                               /payroll/tax-declaration/queue)
 *   /hr                       → /people        (the old HR overview)
 *   /loans                    → /me/loans, or /loans/manage for finance
 *                               (company admin, HR manager, accountant:
 *                               their /loans showed every employee's loans)
 *   /hr/documents             → /me/documents for anyone who is not an HR
 *                               admin (they only ever saw their own store);
 *                               HR stays, the page keeps its employee picker
 *
 * The role-dependent decisions live here, as plain functions, so the node
 * unit tests cover them; the route files only act on the answer.
 *
 * Plain .ts (type-only imports) so `npm run test:unit` loads it.
 */

import { isAccountingAdmin, isHRAdmin } from "@/lib/nav";
import type { Role } from "@/types/auth";

/** /payroll/tax-declaration (the employee view) now lives here. */
export const MY_TAX_PATH = "/app/$tenantSlug/me/tax" as const;

/** /hr (the old HR overview) now lives here. */
export const PEOPLE_PATH = "/app/$tenantSlug/people" as const;

export const MY_LOANS_PATH = "/app/$tenantSlug/me/loans" as const;
export const MANAGE_LOANS_PATH = "/app/$tenantSlug/loans/manage" as const;
export const MY_DOCUMENTS_PATH = "/app/$tenantSlug/me/documents" as const;

export type LoansHome = typeof MY_LOANS_PATH | typeof MANAGE_LOANS_PATH;

/**
 * Where /loans sends a viewer. Finance (company admin, HR manager,
 * accountant — the backend's isLoanFinance, whose GET /loans returns every
 * employee's loans) used /loans as the company-wide list, whose home is now
 * Manage → Payroll → Loans & Advances; everyone else saw only their own
 * loans, now Me → Pay → Loans.
 */
export function loansHomeFor(role: Role | undefined): LoansHome {
  return isAccountingAdmin(role) ? MANAGE_LOANS_PATH : MY_LOANS_PATH;
}

/**
 * Where /hr/documents sends a viewer: null keeps HR admins on the page
 * (it is their company document store with the employee picker); anyone
 * else only ever saw their own documents there, so they go to the Me twin.
 */
export function documentsRedirectFor(
  role: Role | undefined,
): typeof MY_DOCUMENTS_PATH | null {
  return isHRAdmin(role) ? null : MY_DOCUMENTS_PATH;
}
